import { SERVER_ONLY_COOKIE_OPTIONS } from "./cookie-options";

const SHARED_AUTH_COOKIE_DOMAIN = "app.amethy.st";

type UrlLike =
  | string
  | URL
  | {
      hostname?: string;
      href?: string;
      toString(): string;
    };

type HeadersLike = {
  get(name: string): string | null;
};

type RequestLike = {
  headers?: HeadersLike;
  nextUrl?: UrlLike;
  url?: string | URL;
};

export type SharedAuthCookieSource = UrlLike | RequestLike;

function getFirstHeaderValue(
  value: string | null | undefined,
): string | undefined {
  const firstValue = value?.split(",")[0]?.trim();
  return firstValue ? firstValue : undefined;
}

function normalizeHostname(value: string | undefined): string | null {
  if (!value) {
    return null;
  }

  try {
    return new URL(`https://${value}`).hostname;
  } catch {
    return null;
  }
}

function resolveHostname(value: UrlLike | undefined): string | null {
  if (!value) {
    return null;
  }

  if (typeof value === "object" && "hostname" in value) {
    const hostname = value.hostname;
    if (typeof hostname === "string" && hostname.length > 0) {
      return hostname;
    }
  }

  if (value instanceof URL) {
    return value.hostname;
  }

  if (typeof value === "object" && "href" in value) {
    const href = value.href;
    if (typeof href === "string") {
      try {
        return new URL(href).hostname;
      } catch {
        return null;
      }
    }
  }

  try {
    return new URL(String(value)).hostname;
  } catch {
    return null;
  }
}

export function resolveSharedAuthCookieDomain(
  value: SharedAuthCookieSource | undefined,
): string | undefined {
  const candidates: Array<string | null> = [];

  if (value && typeof value === "object" && "headers" in value) {
    candidates.push(
      normalizeHostname(
        getFirstHeaderValue(value.headers?.get("x-forwarded-host")),
      ),
    );
    candidates.push(
      normalizeHostname(getFirstHeaderValue(value.headers?.get("host"))),
    );
    candidates.push(resolveHostname(value.nextUrl));
    candidates.push(resolveHostname(value.url));
  }

  candidates.push(resolveHostname(value));

  for (const hostname of candidates) {
    if (hostname === SHARED_AUTH_COOKIE_DOMAIN) {
      return SHARED_AUTH_COOKIE_DOMAIN;
    }
  }

  return undefined;
}

export function buildSharedAuthCookieOptions(
  value: SharedAuthCookieSource | undefined,
): {
  domain?: string;
} {
  const domain = resolveSharedAuthCookieDomain(value);
  return domain ? { domain } : {};
}

function buildSharedServerCookieOptions(
  value: SharedAuthCookieSource | undefined,
  overrides: SharedServerCookieOverrides = {},
) {
  return {
    ...SERVER_ONLY_COOKIE_OPTIONS,
    ...buildSharedAuthCookieOptions(value),
    ...overrides,
  };
}

type SharedServerCookieOverrides = Partial<
  typeof SERVER_ONLY_COOKIE_OPTIONS
> & {
  maxAge?: number;
};

type SharedServerCookieWriter = {
  set: (
    name: string,
    value: string,
    options: ReturnType<typeof buildSharedServerCookieOptions>,
  ) => unknown;
};

function serializeServerCookie(
  name: string,
  cookieValue: string,
  options: ReturnType<typeof buildSharedServerCookieOptions>,
): string {
  const attributes = [
    `Path=${options.path}`,
    typeof options.maxAge === "number" ? `Max-Age=${options.maxAge}` : null,
    options.domain ? `Domain=${options.domain}` : null,
    options.secure ? "Secure" : null,
    options.httpOnly ? "HttpOnly" : null,
    options.sameSite ? `SameSite=${options.sameSite}` : null,
  ].filter((attribute): attribute is string => Boolean(attribute));

  return [`${name}=${cookieValue}`, ...attributes].join("; ");
}

export function appendClearSharedServerCookieHeaders(
  headers: Headers,
  name: string,
  requestUrl: SharedAuthCookieSource | undefined,
) {
  const sharedOptions = buildSharedServerCookieOptions(requestUrl, {
    maxAge: 0,
  });

  headers.append("Set-Cookie", serializeServerCookie(name, "", sharedOptions));

  if (sharedOptions.domain) {
    const hostOnlyOptions = { ...sharedOptions };
    delete hostOnlyOptions.domain;
    headers.append(
      "Set-Cookie",
      serializeServerCookie(name, "", hostOnlyOptions),
    );
  }
}

export function setSharedServerCookie(
  writer: SharedServerCookieWriter,
  name: string,
  cookieValue: string,
  requestUrl: SharedAuthCookieSource | undefined,
  overrides: SharedServerCookieOverrides = {},
) {
  writer.set(
    name,
    cookieValue,
    buildSharedServerCookieOptions(requestUrl, overrides),
  );
}

export function clearSharedServerCookie(
  writer: SharedServerCookieWriter,
  name: string,
  requestUrl: SharedAuthCookieSource | undefined,
) {
  const sharedOptions = buildSharedServerCookieOptions(requestUrl, {
    maxAge: 0,
  });

  writer.set(name, "", sharedOptions);

  if (sharedOptions.domain) {
    const hostOnlyOptions = { ...sharedOptions };
    delete hostOnlyOptions.domain;
    writer.set(name, "", hostOnlyOptions);
  }
}

/**
 * ホスト名文字列から共有 Cookie ドメインを解決します。
 * クライアントサイド（`window.location.hostname`）で使うことを想定した
 * 軽量版で、ヘッダーや URL オブジェクトの解析は行いません。
 */
export function resolveSharedCookieDomainFromHostname(
  hostname: string,
): string | undefined {
  return hostname === SHARED_AUTH_COOKIE_DOMAIN
    ? SHARED_AUTH_COOKIE_DOMAIN
    : undefined;
}
